import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { DashboardLayout } from '../components/DashboardLayout';
import { StudentInfo } from '../types';
import { LLMConfigModal } from '../components/LLM/LLMConfigModal';
import toast from 'react-hot-toast';
import glassTheme, { glassMixins } from '../styles/glassTheme';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  ${glassTheme.glass.container}
  padding: 2rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 1.1rem;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  ${glassTheme.glass.light}
  padding: 2rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    box-shadow: ${glassTheme.shadow.lg};
    border-color: ${glassTheme.colors.borderHover};
  }
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid ${glassTheme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${glassTheme.colors.text.secondary};
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${glassTheme.gradients.cyber};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: ${glassTheme.colors.text.primary};
  margin: 0 auto 1.5rem;
  box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
`;

const Button = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 0.8rem 2rem;
  background: ${props => props.variant === 'danger'
    ? glassTheme.gradients.error
    : glassTheme.gradients.cyber};
  border: none;
  border-radius: ${glassTheme.borderRadius.full};
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  margin-top: 1rem;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
    filter: brightness(1.1);
  }
`;

const Badge = styled.span<{ type: 'success' | 'warning' | 'info' }>`
  padding: 0.3rem 0.8rem;
  border-radius: ${glassTheme.borderRadius.lg};
  font-size: 0.85rem;
  font-weight: 600;

  ${props => {
    switch (props.type) {
      case 'success':
        return `background: rgba(74, 222, 128, 0.2); color: ${glassTheme.colors.status.success}; border: 1px solid ${glassTheme.colors.status.success};`;
      case 'warning':
        return `background: rgba(251, 191, 36, 0.2); color: ${glassTheme.colors.status.warning}; border: 1px solid ${glassTheme.colors.status.warning};`;
      default:
        return `background: rgba(96, 165, 250, 0.2); color: ${glassTheme.colors.status.info}; border: 1px solid ${glassTheme.colors.status.info};`;
    }
  }}
`;

const EditButton = styled.button`
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  font-size: 0.85rem;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    border-color: transparent;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${glassTheme.colors.text.secondary};
  font-weight: 500;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  ${glassMixins.input}
  width: 100%;
  padding: 0.75rem;
  font-size: 0.9rem;
`;

const FormSelect = styled.select`
  ${glassMixins.input}
  width: 100%;
  padding: 0.75rem;
  font-size: 0.9rem;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SmallButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 0.75rem 1.5rem;
  background: ${props => props.variant === 'secondary'
    ? 'transparent'
    : glassTheme.gradients.cyber};
  border: ${props => props.variant === 'secondary'
    ? `1px solid ${glassTheme.colors.border}`
    : 'none'};
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${glassTheme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateStudentInfo, isLoading, refreshUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<StudentInfo | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showLLMConfig, setShowLLMConfig] = useState(false);

  const handleEdit = () => {
    if (user?.studentInfo) {
      setEditData({ ...user.studentInfo });
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData(null);
  };

  const handleSave = async () => {
    if (!editData) return;

    const success = await updateStudentInfo(editData);
    if (success) {
      const roleName = (() => {
        switch (user?.primaryRole) {
          case 'super_admin': return '超级管理员';
          case 'admin': return '管理员';
          case 'moderator': return '协调员';
          case 'analyst': return '数据分析人员';
          case 'content_manager': return '内容管理员';
          case 'student':
          default: return '学生';
        }
      })();
      toast.success(`${roleName}信息更新成功`);
      setIsEditing(false);
      setEditData(null);
    } else {
      toast.error('更新失败，请重试');
    }
  };

  const handleChange = (field: keyof StudentInfo, value: any) => {
    if (editData) {
      setEditData({ ...editData, [field]: value });
    }
  };

  const getStatsLevelLabel = (level: string) => {
    switch (level) {
      case 'none': return '完全小白';
      case 'beginner': return '一点点';
      case 'basic': return '中等水平';
      case 'intermediate': return '中高等水平';
      case 'advanced': return '专业';
      default: return level;
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('已成功退出登录');
  };

  const handleRefreshUser = async () => {
    setIsRefreshing(true);
    const success = await refreshUser();
    if (success) {
      toast.success('用户数据已更新');
    } else {
      toast.error('更新失败，请重新登录');
    }
    setIsRefreshing(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <DashboardLayout
        title="用户中心"
        subtitle="管理您的个人信息和偏好设置"
      >
        <Container>
          <Card>
            <p style={{ color: glassTheme.colors.text.primary, textAlign: 'center' }}>未登录</p>
          </Card>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="用户中心"
      subtitle="管理您的个人信息和偏好设置"
    >
      <Container>
        <Content>
        {/* Left Column - User Info */}
        <div>
          <Card>
            <Avatar>
              {user.name.charAt(0).toUpperCase()}
            </Avatar>
            <h3 style={{
              color: glassTheme.colors.text.primary,
              textAlign: 'center',
              marginBottom: '1rem'
            }}>
              {user.name}
            </h3>
            <p style={{
              color: glassTheme.colors.text.secondary,
              textAlign: 'center',
              marginBottom: '1.5rem'
            }}>
              {user.email}
            </p>
            <Button variant="danger" onClick={handleLogout}>
              退出登录
            </Button>
          </Card>

          <Card style={{ marginTop: '2rem' }}>
            <CardTitle>账户状态</CardTitle>
            <InfoRow>
              <InfoLabel>登录状态</InfoLabel>
              <Badge type="success">已登录</Badge>
            </InfoRow>
            <InfoRow>
              <InfoLabel>账户类型</InfoLabel>
              <Badge type={
                user.primaryRole === 'teacher' ? 'warning' :
                user.primaryRole === 'super_admin' || user.primaryRole === 'admin' ? 'success' : 'info'
              }>
                {(() => {
                  switch (user.primaryRole) {
                    case 'super_admin': return '超级管理员';
                    case 'admin': return '管理员';
                    case 'teacher': return '教师';
                    case 'moderator': return '协调员';
                    case 'analyst': return '数据分析人员';
                    case 'content_manager': return '内容管理员';
                    case 'student':
                    default: return '学员';
                  }
                })()}
              </Badge>
            </InfoRow>
            <InfoRow>
              <InfoLabel>会员期限</InfoLabel>
              <Badge type="warning">免费版</Badge>
            </InfoRow>
            <Button
              onClick={handleRefreshUser}
              disabled={isRefreshing}
              style={{ marginTop: '1rem', fontSize: '0.85rem', padding: '0.6rem 1rem' }}
            >
              {isRefreshing ? '刷新中...' : '刷新用户数据'}
            </Button>
            {user.isAdmin && (
              <Button
                onClick={() => navigate('/admin')}
                style={{ marginTop: '0.5rem', fontSize: '0.85rem', padding: '0.6rem 1rem' }}
              >
                进入管理后台
              </Button>
            )}
          </Card>
        </div>

        {/* Right Column - Details */}
        <div>
          <Card>
            <CardTitle>账户信息</CardTitle>
            <InfoRow>
              <InfoLabel>用户ID</InfoLabel>
              <InfoValue style={{ fontSize: '0.9rem' }}>
                {user.id}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>用户名</InfoLabel>
              <InfoValue>{user.name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>邮箱地址</InfoLabel>
              <InfoValue>{user.email}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>注册时间</InfoLabel>
              <InfoValue>{formatDate(user.createdAt)}</InfoValue>
            </InfoRow>
          </Card>

          {/* 学生信息 - 仅对学生角色显示 */}
          {user.primaryRole === 'student' && user.studentInfo && (
            <Card style={{ marginTop: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: `1px solid ${glassTheme.colors.border}` }}>
                <CardTitle style={{ marginBottom: 0, paddingBottom: 0, border: 'none' }}>学生信息</CardTitle>
                {!isEditing && (
                  <EditButton onClick={handleEdit}>编辑</EditButton>
                )}
              </div>

              {isEditing && editData ? (
                <FormGrid>
                  <FormGroup>
                    <FormLabel>学号</FormLabel>
                    <FormInput
                      type="text"
                      value={editData.studentId}
                      onChange={(e) => handleChange('studentId', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>真实姓名</FormLabel>
                    <FormInput
                      type="text"
                      value={editData.realName}
                      onChange={(e) => handleChange('realName', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>性别</FormLabel>
                    <FormSelect
                      value={editData.gender}
                      onChange={(e) => handleChange('gender', e.target.value as 'male' | 'female' | '')}
                    >
                      <option value="male">男</option>
                      <option value="female">女</option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>出生年月</FormLabel>
                    <FormInput
                      type="month"
                      value={editData.birthDate}
                      onChange={(e) => handleChange('birthDate', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>年级</FormLabel>
                    <FormSelect
                      value={editData.grade}
                      onChange={(e) => handleChange('grade', e.target.value)}
                    >
                      <option value="大一">大一</option>
                      <option value="大二">大二</option>
                      <option value="大三">大三</option>
                      <option value="大四">大四</option>
                      <option value="研一">研一</option>
                      <option value="研二">研二</option>
                      <option value="研三">研三</option>
                      <option value="博士">博士</option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>学院</FormLabel>
                    <FormInput
                      type="text"
                      value={editData.college}
                      onChange={(e) => handleChange('college', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup className={FullWidth}>
                    <FormLabel>专业</FormLabel>
                    <FormInput
                      type="text"
                      value={editData.major}
                      onChange={(e) => handleChange('major', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>是否学过统计课程</FormLabel>
                    <FormSelect
                      value={editData.hasStatsBackground?.toString()}
                      onChange={(e) => handleChange('hasStatsBackground', e.target.value === 'true')}
                    >
                      <option value="true">是</option>
                      <option value="false">否</option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>统计学知识水平</FormLabel>
                    <FormSelect
                      value={editData.statsLevel}
                      onChange={(e) => handleChange('statsLevel', e.target.value as StudentInfo['statsLevel'])}
                    >
                      <option value="none">完全小白</option>
                      <option value="beginner">一点点</option>
                      <option value="basic">中等水平</option>
                      <option value="intermediate">中高等水平</option>
                      <option value="advanced">专业</option>
                    </FormSelect>
                  </FormGroup>
                  <FullWidth>
                    <ButtonGroup>
                      <SmallButton variant="secondary" onClick={handleCancel} disabled={isLoading}>
                        取消
                      </SmallButton>
                      <SmallButton onClick={handleSave} disabled={isLoading}>
                        {isLoading ? '保存中...' : '保存'}
                      </SmallButton>
                    </ButtonGroup>
                  </FullWidth>
                </FormGrid>
              ) : (
                <>
                  <InfoRow>
                    <InfoLabel>学号</InfoLabel>
                    <InfoValue>{user.studentInfo.studentId}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>真实姓名</InfoLabel>
                    <InfoValue>{user.studentInfo.realName}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>性别</InfoLabel>
                    <InfoValue>{user.studentInfo.gender === 'male' ? '男' : '女'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>出生年月</InfoLabel>
                    <InfoValue>{user.studentInfo.birthDate}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>年级</InfoLabel>
                    <InfoValue>{user.studentInfo.grade}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>学院</InfoLabel>
                    <InfoValue>{user.studentInfo.college}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>专业</InfoLabel>
                    <InfoValue>{user.studentInfo.major}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>学过统计课程</InfoLabel>
                    <Badge type={user.studentInfo.hasStatsBackground ? 'success' : 'info'}>
                      {user.studentInfo.hasStatsBackground ? '是' : '否'}
                    </Badge>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>统计学水平</InfoLabel>
                    <InfoValue>{getStatsLevelLabel(user.studentInfo.statsLevel)}</InfoValue>
                  </InfoRow>
                </>
              )}
            </Card>
          )}

          {/* 管理员/工作人员信息 - 对非学生角色显示 */}
          {user.primaryRole !== 'student' && (
            <Card style={{ marginTop: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: `1px solid ${glassTheme.colors.border}` }}>
                <CardTitle style={{ marginBottom: 0, paddingBottom: 0, border: 'none' }}>
                  {(() => {
                    switch (user.primaryRole) {
                      case 'super_admin': return '超级管理员信息';
                      case 'admin': return '管理员信息';
                      case 'moderator': return '协调员信息';
                      case 'analyst': return '数据分析人员信息';
                      case 'content_manager': return '内容管理员信息';
                      default: return '工作人员信息';
                    }
                  })()}
                </CardTitle>
                {!isEditing && (
                  <EditButton onClick={handleEdit}>编辑</EditButton>
                )}
              </div>

              {isEditing && editData ? (
                <FormGrid>
                  <FormGroup>
                    <FormLabel>真实姓名</FormLabel>
                    <FormInput
                      type="text"
                      value={editData.realName || ''}
                      onChange={(e) => handleChange('realName', e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>性别</FormLabel>
                    <FormSelect
                      value={editData.gender || ''}
                      onChange={(e) => handleChange('gender', e.target.value as 'male' | 'female' | '')}
                    >
                      <option value="">请选择</option>
                      <option value="male">男</option>
                      <option value="female">女</option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>出生年月</FormLabel>
                    <FormInput
                      type="month"
                      value={editData.birthDate || ''}
                      onChange={(e) => handleChange('birthDate', e.target.value)}
                    />
                  </FormGroup>
                  {(user.primaryRole === 'super_admin' || user.primaryRole === 'admin') && (
                    <>
                      <FormGroup>
                        <FormLabel>学院</FormLabel>
                        <FormInput
                          type="text"
                          value={editData.college || ''}
                          onChange={(e) => handleChange('college', e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel>专业</FormLabel>
                        <FormInput
                          type="text"
                          value={editData.major || ''}
                          onChange={(e) => handleChange('major', e.target.value)}
                        />
                      </FormGroup>
                    </>
                  )}
                  <FullWidth>
                    <ButtonGroup>
                      <SmallButton variant="secondary" onClick={handleCancel} disabled={isLoading}>
                        取消
                      </SmallButton>
                      <SmallButton onClick={handleSave} disabled={isLoading}>
                        {isLoading ? '保存中...' : '保存'}
                      </SmallButton>
                    </ButtonGroup>
                  </FullWidth>
                </FormGrid>
              ) : (
                <>
                  <InfoRow>
                    <InfoLabel>真实姓名</InfoLabel>
                    <InfoValue>{user.studentInfo?.realName || '未填写'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>性别</InfoLabel>
                    <InfoValue>
                      {user.studentInfo?.gender === 'male' ? '男' : user.studentInfo?.gender === 'female' ? '女' : '未填写'}
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>出生年月</InfoLabel>
                    <InfoValue>{user.studentInfo?.birthDate || '未填写'}</InfoValue>
                  </InfoRow>
                  {(user.primaryRole === 'super_admin' || user.primaryRole === 'admin') && (
                    <>
                      <InfoRow>
                        <InfoLabel>学院</InfoLabel>
                        <InfoValue>{user.studentInfo?.college || '未填写'}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>专业</InfoLabel>
                        <InfoValue>{user.studentInfo?.major || '未填写'}</InfoValue>
                      </InfoRow>
                    </>
                  )}
                </>
              )}
            </Card>
          )}

          {/* 学习统计 - 仅对学生显示 */}
          {user.primaryRole === 'student' && (
            <Card style={{ marginTop: '2rem' }}>
              <CardTitle>学习统计</CardTitle>
              <InfoRow>
                <InfoLabel>已完成概念</InfoLabel>
                <InfoValue>
                  {user.learningProgress?.conceptsCompleted?.length || 0} 个
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>已完成练习</InfoLabel>
                <InfoValue>
                  {user.learningProgress?.exercisesCompleted?.length || 0} 个
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>学习时长</InfoLabel>
                <InfoValue>
                  {Math.floor((user.learningProgress?.timeSpent || 0) / 60)} 分钟
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>最后访问</InfoLabel>
                <InfoValue>
                  {user.learningProgress?.lastAccessTime
                    ? formatDate(user.learningProgress.lastAccessTime)
                    : '暂无数据'
                  }
                </InfoValue>
              </InfoRow>
            </Card>
          )}

          {/* 工作统计 - 对管理员角色显示 */}
          {user.primaryRole !== 'student' && (
            <Card style={{ marginTop: '2rem' }}>
              <CardTitle>
                {(() => {
                  switch (user.primaryRole) {
                    case 'super_admin':
                    case 'admin':
                      return '管理统计';
                    case 'moderator':
                      return '审核统计';
                    case 'analyst':
                      return '数据分析统计';
                    case 'content_manager':
                      return '内容管理统计';
                    default:
                      return '工作统计';
                  }
                })()}
              </CardTitle>
              <InfoRow>
                <InfoLabel>最后访问</InfoLabel>
                <InfoValue>
                  {user.learningProgress?.lastAccessTime
                    ? formatDate(user.learningProgress.lastAccessTime)
                    : '暂无数据'
                  }
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>账户状态</InfoLabel>
                <Badge type="success">活跃</Badge>
              </InfoRow>
              {user.isAdmin && (
                <InfoRow>
                  <InfoLabel>管理权限</InfoLabel>
                  <Badge type="success">
                    {user.primaryRole === 'super_admin' ? '完全控制' : '部分管理'}
                  </Badge>
                </InfoRow>
              )}
            </Card>
          )}

          <Card style={{ marginTop: '2rem' }}>
            <CardTitle>LLM 配置</CardTitle>
            <p style={{ color: glassTheme.colors.text.secondary, fontSize: '0.9rem', marginBottom: '1rem' }}>
              配置您自己的LLM API密钥，使用您喜欢的AI模型。如果不配置，将使用平台默认服务。
            </p>
            <Button onClick={() => setShowLLMConfig(true)}>
              <i className="fas fa-cog" style={{ marginRight: '0.5rem' }}></i>
              配置LLM API
            </Button>
          </Card>

          <Card style={{ marginTop: '2rem' }}>
            <CardTitle>安全设置</CardTitle>
            <Button onClick={() => toast('修改密码功能即将上线')}>
              修改密码
            </Button>
            <Button
              onClick={() => toast('双因素认证功能即将上线')}
              style={{ marginTop: '1rem' }}
            >
              启用双因素认证
            </Button>
          </Card>
        </div>
      </Content>
      </Container>

      <LLMConfigModal
        visible={showLLMConfig}
        onClose={() => setShowLLMConfig(false)}
        onConfigSaved={() => {
          toast.success('LLM配置已更新');
        }}
      />
    </DashboardLayout>
  );
};
